import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Cloud, Database, Server, ShieldCheck, Globe, Cpu } from "lucide-react";

// --- Helper Components ---

const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${i <= rating ? "text-yellow-500" : "text-gray-300"} inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 ml-2 border border-gray-300">
    <ShieldCheck className="w-3 h-3 mr-1 text-blue-600" />
    Verified
  </span>
);

const ToolsYouWillMaster = () => {
  const tools = [
    { name: 'Google Cloud', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/googlecloud/googlecloud-original.svg' },
    { name: 'Compute Engine', logo: 'https://upload.wikimedia.org/wikipedia/commons/e/e0/Google_Compute_Engine_logo.svg' }, // Generic placeholder
    { name: 'Kubernetes (GKE)', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/kubernetes/kubernetes-plain.svg' },
    { name: 'BigQuery', logo: 'https://upload.wikimedia.org/wikipedia/commons/3/36/Google_BigQuery_Logo.svg' }, // Generic placeholder
    { name: 'App Engine', logo: 'https://upload.wikimedia.org/wikipedia/commons/e/e7/Google_App_Engine_Logo.svg' }, // Generic
    { name: 'Cloud Storage', logo: 'https://upload.wikimedia.org/wikipedia/commons/3/30/Google_Cloud_Storage_Logo.svg' }, // Generic
    { name: 'Terraform', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/terraform/terraform-original.svg' },
    { name: 'Docker', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg' },
  ];

  return (
    <section className="py-20 bg-gray-100">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-black font-aligran">
          GCP Services <span className="text-blue-600">Mastery</span>
        </h2>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-8 mb-8">
          {tools.map((tool, i) => (
            <motion.div
              key={i}
              className="flex flex-col items-center justify-center p-4 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all hover:scale-105"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <img src={tool.logo} alt={tool.name} className="w-16 h-16 mb-2 object-contain" />
              <p className="text-sm font-semibold text-black mt-2">{tool.name}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const GCPCourse = () => {
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [formData, setFormData] = useState({ firstName: "", businessEmail: "", phone: "" });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert("Inquiry Sent!");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonialIndex((prev) => (prev + 1) % 5);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const testimonials = [
    { id: 1, name: "Arun Kumar", review: "GKE (Kubernetes Engine) module is the best. I learned how to deploy scalable apps on Google Cloud.", rating: 5, profileImg: "https://randomuser.me/api/portraits/men/10.jpg", source: "LinkedIn" },
    { id: 2, name: "Priya Sethi", review: "BigQuery and Data Engineering concepts were explained simply. Great for data roles.", rating: 5, profileImg: "https://randomuser.me/api/portraits/women/11.jpg", source: "Google Reviews" },
    { id: 3, name: "Rahul Verma", review: "I cleared my Associate Cloud Engineer exam after this course. The mock tests helped a lot.", rating: 4, profileImg: "https://randomuser.me/api/portraits/men/22.jpg", source: "Website Feedback" },
    { id: 4, name: "Sneha Das", review: "IAM and Security policies were tough, but the practical labs made it easy to understand.", rating: 5, profileImg: "https://randomuser.me/api/portraits/women/33.jpg", source: "LinkedIn" },
    { id: 5, name: "Vikram Singh", review: "Comparing AWS and GCP features helped me migrate my company's project. Highly recommended.", rating: 4, profileImg: "https://randomuser.me/api/portraits/men/44.jpg", source: "Google Reviews" },
  ];

  const currentVisibleTestimonials = [
    testimonials[currentTestimonialIndex],
    testimonials[(currentTestimonialIndex + 1) % 5],
    testimonials[(currentTestimonialIndex + 2) % 5],
  ];

  const curriculum = [
    { title: "Module 1: GCP Fundamentals", description: "Google Global Infrastructure, Regions, Zones, and Creating a GCP Account." },
    { title: "Module 2: Compute Engine", description: "Virtual Machines, Preemptible VMs, Load Balancing, and Auto-scaling." },
    { title: "Module 3: Storage Services", description: "Cloud Storage (Buckets), Persistent Disks, Filestore, and Lifecycle Management." },
    { title: "Module 4: Google Kubernetes Engine (GKE)", description: "Deploying containerized apps, Clusters, Nodes, and Pods management." },
    { title: "Module 5: App Engine & Cloud Functions", description: "Serverless computing, deploying code without managing servers." },
    { title: "Module 6: Networking (VPC)", description: "VPC, Subnets, Firewall Rules, Cloud VPN, and Interconnect." },
    { title: "Module 7: Identity & Security (IAM)", description: "Managing Users, Roles, Service Accounts, and Cloud Security Scanner." },
    { title: "Module 8: Big Data & AI Services", description: "Intro to BigQuery, Dataflow, Pub/Sub, and Cloud Vision API." },
    { title: "Module 9: Monitoring & DevOps", description: "Cloud Operations (Stackdriver), Logging, Monitoring, and CI/CD pipelines." },
  ];

  const highlights = [
    { title: 'Google Power', desc: 'Run on Google infrastructure.', img: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?q=80&w=2069' },
    { title: 'Kubernetes King', desc: 'Master GKE (The standard).', img: 'https://images.unsplash.com/photo-1667372393119-c85c020799a3?q=80&w=2070' },
    { title: 'Big Data Ready', desc: 'BigQuery for Analytics.', img: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070' },
    { title: 'Serverless', desc: 'Cloud Functions & Run.', img: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=2070' },
    { title: 'Certification', desc: 'ACE & Architect Prep.', img: 'https://images.unsplash.com/photo-1589330273594-fade1ee91647?q=80&w=2070' },
    { title: 'Networking', desc: 'Global VPC Design.', img: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?q=80&w=2070' },
    { title: 'Expert Mentors', desc: 'Google Cloud Certified.', img: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=2070' },
    { title: 'Live Labs', desc: 'Hands-on Qwiklabs style.', img: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?q=80&w=2070' },
    { title: 'Placement', desc: 'Jobs in Data & Cloud.', img: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=2069' },
  ];

  return (
    <div className="bg-white text-gray-900 font-sans">
      <Helmet>
        <title>GCP Course | Google Cloud Associate Engineer</title>
        <meta name="description" content="Master Google Cloud Platform. Learn GKE, Compute Engine, BigQuery and App Engine." />
      </Helmet>

      {/* Hero */}
      <section className="relative h-[550px] flex items-center bg-black">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1484557985045-6f5c50f89fc2?q=80&w=2069" alt="GCP Cloud" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-blue-900/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center w-full pt-20">
          <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }}>
            <span className="inline-block px-3 py-1 mb-4 border border-blue-500 rounded-full bg-blue-900/30 text-blue-400 text-sm font-semibold">DATA CLOUD LEADER</span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white font-aligran">Master <span className="text-blue-500 block">Google Cloud (GCP)</span></h1>
            <p className="text-xl text-gray-300 mt-6 mb-8">Learn the cloud platform built by Google. Master Kubernetes, Big Data, and Machine Learning services.</p>
            <button onClick={() => document.getElementById("enquiry-form")?.scrollIntoView({ behavior: "smooth" })} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-10 rounded-lg shadow-lg hover:scale-105 transition">Start Learning</button>
          </motion.div>
          <motion.div className="flex justify-end"><Cloud size={220} className="text-blue-500 opacity-80 drop-shadow-lg" /></motion.div>
        </div>
      </section>

      {/* Why Learn */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50 text-center">
        <h2 className="text-4xl font-extrabold mb-14 font-aligran">Why Learn <span className="text-blue-600">GCP?</span></h2>
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-8">
          <div className="p-8 border border-gray-100 rounded-2xl bg-white shadow-md hover:-translate-y-2 transition duration-300">
            <Globe className="mx-auto mb-4 text-blue-600" size={40} /><h3 className="font-bold text-xl mb-3">Fastest Network</h3><p className="text-gray-600">GCP runs on the same infrastructure that powers Google Search and YouTube.</p>
          </div>
          <div className="p-8 border border-gray-100 rounded-2xl bg-white shadow-md hover:-translate-y-2 transition duration-300">
            <Server className="mx-auto mb-4 text-red-500" size={40} /><h3 className="font-bold text-xl mb-3">Kubernetes Leader</h3><p className="text-gray-600">Google invented Kubernetes. GKE is the most advanced managed K8s service.</p>
          </div>
          <div className="p-8 border border-gray-100 rounded-2xl bg-white shadow-md hover:-translate-y-2 transition duration-300">
            <Database className="mx-auto mb-4 text-yellow-500" size={40} /><h3 className="font-bold text-xl mb-3">Big Data & AI</h3><p className="text-gray-600">Best-in-class tools for Data Analytics (BigQuery) and AI/ML projects.</p>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-gray-900 font-aligran">Course <span className="text-blue-600">Highlights</span></h2>
          <div className="grid md:grid-cols-3 gap-8">
            {highlights.map((h, i) => (
              <div key={i} className="bg-white rounded-xl shadow-md overflow-hidden flex h-40 hover:shadow-xl transition hover:-translate-y-1">
                <div className="w-1/2 p-4 flex flex-col justify-center"><h3 className="font-bold text-lg">{h.title}</h3><p className="text-sm text-gray-600">{h.desc}</p></div>
                <div className="w-1/2 relative"><img src={h.img} alt={h.title} className="w-full h-full object-cover" /></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="py-20 bg-white"><div className="max-w-5xl mx-auto px-6"><h2 className="text-3xl font-bold mb-12 font-aligran">Detailed <span className="text-blue-600">Curriculum</span></h2><div className="space-y-4">{curriculum.map((m, i) => (<details key={i} className="group border rounded-lg bg-gray-50"><summary className="cursor-pointer px-6 py-4 font-semibold flex justify-between">{m.title}<span className="group-open:rotate-180 transition">▼</span></summary><div className="px-6 py-4 border-t text-gray-700">{m.description}</div></details>))}</div></div></section>

      <ToolsYouWillMaster />

      {/* FAQ & Form */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2"><h2 className="text-3xl font-bold mb-8 font-aligran">FAQ</h2><div className="space-y-4"><details className="bg-gray-50 border p-4 rounded-lg"><summary className="font-bold">AWS vs GCP?</summary><p className="mt-2 text-sm">AWS is market leader. GCP is best for Data, AI, and Containerization.</p></details><details className="bg-gray-50 border p-4 rounded-lg"><summary className="font-bold">Is coding needed?</summary><p className="mt-2 text-sm">Minimal. Basic Shell scripting helps, but mostly configuration based.</p></details></div></div>
        <div className="md:col-span-1" id="enquiry-form"><div className="bg-gray-100 border border-blue-300 rounded-xl p-6 shadow-xl sticky top-10"><h3 className="text-2xl font-bold mb-2 text-center text-blue-600">Book Seat</h3><form onSubmit={handleSubmit} className="space-y-4"><input className="w-full border p-3 rounded" placeholder="Name" name="firstName" onChange={handleInputChange} /><input className="w-full border p-3 rounded" placeholder="Email" name="businessEmail" onChange={handleInputChange} /><input className="w-full border p-3 rounded" placeholder="Phone" name="phone" onChange={handleInputChange} /><button className="w-full bg-blue-600 text-white font-bold py-3 rounded hover:bg-blue-700 transition">ENQUIRE</button></form></div></div>
      </section>

      {/* Testimonials */}
      <section className="bg-gray-50 py-20 border-t">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-12 font-aligran">Success <span className="text-blue-600">Stories</span></h2>
          <div className="grid md:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {currentVisibleTestimonials.map((t) => (
                <motion.div key={t.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white p-6 rounded-xl border shadow-sm"><div className="flex items-center mb-4"><img src={t.profileImg} className="w-12 h-12 rounded-full border-2 border-blue-600 mr-3" alt={t.name} /><div><h4 className="font-bold">{t.name}</h4><span className="text-xs text-gray-500">{t.source}</span></div></div><StarRating rating={t.rating} /><p className="text-gray-700 text-sm mt-2">"{t.review}"</p><VerifiedBadge /></motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </section>
    </div>
  );
};
export default GCPCourse;